/**
 * No workspace (Figma 09 · No workspace 01–09): a signed-in person who belongs
 * to no workspace yet. Invitations to accept or decline, and their account.
 * Not built, no backend yet: Undo on a decline, ended invitations, and
 * "Removed from a workspace".
 */
import { useState, type ReactNode } from "react";
import { Hourglass, LogOut } from "lucide-react";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useResolvedTheme } from "@/stores/appearanceStore";
import { useAcceptInvite, useDeclineInvite, useMyInvites } from "@/app/routes/dashboard/settings/api/useOrganization";
import type { IncomingOrganizationInvite } from "@/types/organization.type";
import { KitButton } from "../kit/Button";
import { ErrorState, Loading } from "../ViewStates";
import { DetailCard, DetailHead, Fact, Facts, GlyphTile, PersonMark, QuietNote, ScopeTile, StatusLabel } from "./parts/SettingsParts";
import { roleForYou, takesSeat } from "./parts/roles";
import { formatDay, listJoin } from "./parts/settingsFormat";
import { SignOutConfirm } from "@/components/gettingIn/SessionDialogs";
import { CoShellPortalContext } from "../../CoShellPortalContext";

function InvitationCard({
  invite,
  onAccept,
  onDecline,
}: {
  invite: IncomingOrganizationInvite;
  onAccept: () => void;
  onDecline: () => void;
}) {
  const inviter = invite.inviter.name || invite.inviter.email || "Someone";
  const brands = (invite.brands ?? []).map((b) => b.name);
  return (
    <DetailCard>
      <DetailHead
        lead={<ScopeTile name={invite.organization.name} size={56} />}
        title={invite.organization.name}
        sub={`Invitation from ${inviter}`}
        aside={<StatusLabel icon={Hourglass}>Waiting for you</StatusLabel>}
      />
      <Facts>
        <Fact label="You would be">{roleForYou(invite.role)}</Fact>
        {brands.length ? <Fact label="Brands">{listJoin(brands)}</Fact> : null}
        <Fact label="Invited by">{[invite.inviter.name, invite.inviter.email].filter(Boolean).join(", ") || inviter}</Fact>
        <Fact label="Ends">{formatDay(invite.expiresAt)}</Fact>
      </Facts>
      <div className="flex items-center gap-3">
        <KitButton onClick={onAccept}>Accept</KitButton>
        <KitButton variant="ghost" onClick={onDecline}>
          Decline
        </KitButton>
      </div>
      <QuietNote>
        {takesSeat(invite.role)
          ? `Accepting opens ${invite.organization.name} and takes a seat there.`
          : `Accepting opens ${invite.organization.name}. A client takes no seat.`}
      </QuietNote>
    </DetailCard>
  );
}

/**
 * The page frame shared by the screens that stand in for the cockpit when the
 * person has nowhere to work yet (no workspace, no brand): who is signed in,
 * and one centred column.
 */
export function GateFrame({ children }: { children: ReactNode }) {
  const theme = useResolvedTheme();
  const { user } = useCurrentUser();
  const [portal, setPortal] = useState<HTMLDivElement | null>(null);
  const name = user?.name || user?.email || "";

  return (
    <CoShellPortalContext.Provider value={portal}>
      <div ref={setPortal} className={`co-shell co-page${theme === "dark" ? " dark" : ""} flex-col bg-co-canvas`}>
        <header className="flex items-center gap-3 px-5 py-4 md:px-8">
          <PersonMark name={name} picture={user?.profilePicture} size={36} />
          <div className="flex flex-col">
            <span className="text-[15px] font-semibold text-co-text">Creo</span>
            <span className="text-[12px] text-co-muted">Signed in as {user?.email}</span>
          </div>
        </header>
        <main className="mx-auto flex w-full max-w-[640px] flex-1 flex-col gap-5 px-4 pb-12 pt-6 md:pt-14">{children}</main>
      </div>
    </CoShellPortalContext.Provider>
  );
}

export function GateHeading({ heading, sub }: { heading: string; sub: string }) {
  const { user } = useCurrentUser();
  const name = user?.name || user?.email || "";
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-3">
        <PersonMark name={name} picture={user?.profilePicture} size={36} />
        <h1 className="font-section text-[30px] font-bold leading-[1.1] tracking-normal text-co-text">{heading}</h1>
      </div>
      <p className="text-[13.5px] leading-[1.5] text-co-text2">{sub}</p>
    </div>
  );
}

/** Who they are signed in as, and the way out. */
export function GateAccountNav({ onLogout }: { onLogout: () => void }) {
  const { user } = useCurrentUser();
  const [signingOut, setSigningOut] = useState(false);
  const name = user?.name || user?.email || "";
  return (
    <>
      <nav className="flex flex-col gap-0.5 rounded-panel bg-co-surface p-2">
        <p className="px-3 pb-1 pt-2.5 text-[12px] font-semibold text-co-muted">Your account</p>
        <div className="flex items-center gap-3 px-3 py-2.5">
          <PersonMark name={name} picture={user?.profilePicture} />
          <span className="flex min-w-0 flex-col gap-0.5">
            <span className="text-[13.5px] font-semibold text-co-text">{user?.name || "Profile"}</span>
            <span className="truncate text-[12px] text-co-muted">
              {user?.email} · signs in with {user?.hasPassword ? "a password" : "Google"}
            </span>
          </span>
        </div>
        <button
          type="button"
          onClick={() => setSigningOut(true)}
          className="flex items-center gap-3 rounded-row px-3 py-2.5 text-left transition-colors hover:bg-co-hover"
        >
          <GlyphTile icon={LogOut} />
          <span className="flex flex-col gap-0.5">
            <span className="text-[13.5px] font-semibold text-co-text">Sign out</span>
            <span className="text-[12px] text-co-muted">On this device</span>
          </span>
        </button>
      </nav>
      <SignOutConfirm isOpen={signingOut} onOpenChange={setSigningOut} onConfirm={onLogout} />
    </>
  );
}

export default function NoWorkspaceScreen({ onLogout }: { onLogout: () => void }) {
  const { user } = useCurrentUser();
  const { data: invites, isLoading, isError, refetch } = useMyInvites();
  const accept = useAcceptInvite();
  const decline = useDeclineInvite();
  const [opening, setOpening] = useState<string | null>(null);
  const pending = invites ?? [];

  const heading =
    pending.length === 0 ? "No invitations yet" : pending.length === 1 ? "An invitation is waiting" : `${pending.length} invitations are waiting`;
  const sub =
    pending.length === 0
      ? `Creo opens once someone invites ${user?.email ?? "you"} to their workspace. Ask whoever runs yours.`
      : pending.length === 1
        ? "Accept it to open the workspace."
        : "You can accept more than one, and switch between them from the rail.";

  return (
    <GateFrame>
      {opening ? (
        <Loading label={`Opening ${opening}`} minHeight={320} />
      ) : (
        <>
          <GateHeading heading={heading} sub={sub} />

          {isLoading ? (
            <Loading label="Loading your invitations" minHeight={220} />
          ) : isError ? (
            <ErrorState
              title="Your invitations did not load"
              body="Creo could not read them. Nothing has changed."
              onRetry={() => void refetch()}
              minHeight={260}
            />
          ) : (
            pending.map((invite) => (
              <InvitationCard
                key={invite.id}
                invite={invite}
                onAccept={() => {
                  setOpening(invite.organization.name);
                  accept.mutate(invite.id, { onError: () => setOpening(null) });
                }}
                onDecline={() => decline.mutate(invite.id)}
              />
            ))
          )}

          <GateAccountNav onLogout={onLogout} />
        </>
      )}
    </GateFrame>
  );
}
